import { useState, useCallback, useMemo, useEffect } from 'react'
import { useMutation, gql, useQuery, useReactiveVar } from '@apollo/client'
import { checkIsLoggedIn } from '../account'

import client from '@@partner:graphql/client'
import {
  partnerInfoVar,
  currentBrandVar,
  persistCurrentBrand,
  DEFAULT_PARTNER_INFO
} from '@@partner:graphql/initial-data'

import { GQL_QUERY_PING_AUTH } from '@@partner:graphql/query/partner/pingAuth.gql'
import { useMutateUpdateSettings } from '../../graphql/mutation/manager/updateSettings.gql'

/*
  Customer hook
  returns customer local info and func to set new data
*/

export const usePartner = () => {
  // current operatorInfo
  const partnerInfo = useReactiveVar(partnerInfoVar)
  const currentBrand = useReactiveVar(currentBrandVar)

  const [ logoutOnServer ] = useMutation(
    gql`
      mutation {
        logout
      }
    `
  )

  // is current operator logged in
  const isLoggedIn = useMemo(() => {
    return checkIsLoggedIn(partnerInfo)
  }, [ partnerInfo ])

  const setCurrentBrand = useCallback(async (brand: typeof currentBrand) => {
    currentBrandVar(brand)
    persistCurrentBrand(brand)
    // Сбрасываем кэш и перезагружаем все активные запросы
    await client.resetStore()
  }, [])

  const setPartnerInfo = useCallback((info) => {
    partnerInfoVar({
      ...partnerInfoVar(),
      ...info
    })
  }, [])

  // Автоматически выбираем первый доступный бренд при загрузке данных пользователя
  useEffect(() => {
    const brands = partnerInfo.brands || []
    if (brands.length === 0) return

    // Проверяем, что текущий бренд доступен в списке
    const isCurrentBrandAvailable = currentBrand && brands.some(
      (brand) => brand.id === currentBrand.id || brand.slug === currentBrand.slug
    )

    // Если текущий бренд не установлен или недоступен, выбираем первый
    if (!isCurrentBrandAvailable) {
      const firstBrand = brands[0]
      if (firstBrand) {
        currentBrandVar({ id: firstBrand.id, slug: firstBrand.slug })
        persistCurrentBrand({ id: firstBrand.id, slug: firstBrand.slug })
      }
    }
  }, [ partnerInfo.brands, currentBrand ])

  const resetPartnerInfo = useCallback(() => {
    setPartnerInfo(DEFAULT_PARTNER_INFO)
  }, [ setPartnerInfo ])

  // persisting operator mutation
  // const [ persistGQLOperatorInfo, { loading, error } ] = useMutation(
  //   GQL_MUTATION_PERSIST_OPERATOR_INFO
  // )

  // const persistOperatorInfo = useCallback(
  //   (info) => {
  //     return persistGQLOperatorInfo({
  //       variables: {
  //         info
  //       }
  //     }).then(({ data: { persistOperatorInfo } }) =>
  //       setOperatorInfo(mergeLeft(operatorInfo, persistOperatorInfo))
  //     )
  //   },
  //   [ operatorInfo, persistGQLOperatorInfo, setOperatorInfo ]
  // )

  const logout = useCallback(() => {
    return logoutOnServer()
      .then(({ data: { logout } }) => {
        if (logout) {
          resetPartnerInfo()
        }
      })
      .catch((e) => {
        console.error(e)
      })
  }, [ logoutOnServer, resetPartnerInfo ])

  return {
    partnerInfo,
    setPartnerInfo,
    currentBrand,
    setCurrentBrand,
    /** 
     * Slug текущего бренда. 
     * Возвращает undefined если бренд ещё не загружен.
     */
    brandSlug: currentBrand?.slug,
    /** Проверка загружен ли бренд */
    isBrandLoaded: !!currentBrand?.slug,
    canAssemble: !!partnerInfo.assembler_id,
    // persistOperatorInfo,
    // updateLoading: loading,
    // updateError: error,
    isLoggedIn,
    resetPartnerInfo,
    logout
  }
}

export const usePartnerSettings = () => {
  const { partnerInfo, setPartnerInfo } = usePartner()
  const [ saveSettingsGQL, { loading, error } ] = useMutateUpdateSettings()
  const [ touchedSettingKeys, setTouchedSettingKeys ] = useState<string[]>([])

  const settings = useMemo(() => partnerInfo?.settings ?? {}, [ partnerInfo ])

  const updateSettings = useCallback(
    (newSettings: Record<string, any>, newSettingsJson?: string) => {
      setTouchedSettingKeys(Object.keys(newSettings))

      const settingsJson = newSettingsJson ?? JSON.stringify(newSettings)

      setPartnerInfo({
        settings: newSettings,
        settingsJson
      })

      saveSettingsGQL({ variables: { settings: settingsJson } }).then(() => {
        setTouchedSettingKeys([])
      })
    },
    [ saveSettingsGQL, setPartnerInfo ]
  )

  const getSetting = useCallback(
    (name: string, defaultValue: any) => settings[name] ?? defaultValue,
    [ settings ]
  )

  const updateSetting = useCallback(
    (name: string, value: any) => updateSettings({ [name]: value }),
    [ updateSettings ]
  )

  const isSettingLoading = useCallback(
    (name: string) => touchedSettingKeys.includes(name),
    [ touchedSettingKeys ]
  )

  return {
    settings,
    getSetting,
    updateSetting,
    updateSettings,
    touchedSettingKeys,
    isSettingLoading
  }
}

/*
  Check current operator status on server
*/

export const usePingAuth = () => {
  const { data, loading, refetch, error } = useQuery(GQL_QUERY_PING_AUTH, {
    fetchPolicy: 'network-only'
  })

  return {
    error,
    loading,
    refetchCurrentPartnerInfo: refetch,
    currentPartnerInfo: data?.pingAuth
  }
}
